// WU-2.3 — inbox source of truth: fetch-on-login + WS merge on the private
// user channel (useAnnouncements model). Safe to mount multiple instances
// (AppShell badge / inbox page / VS card) — the acked/retracted events
// converge them without shared context. No client-side audience gate is
// needed (unlike announcements) — the private per-user channel already
// scopes delivery server-side.
import { useEffect, useRef, useState, useCallback } from "react";
import type { WsClient } from "@/lib/ws-client";
import { isDirectedNoteActive, type DirectedNoteRecord } from "@capture/shared";
import { directedNotesApi } from "../api/client";

export function useDirectedNotes(wsClient: WsClient | null): {
  notes: DirectedNoteRecord[];
  activeCount: number;
  acknowledge: (id: string, reply?: string) => Promise<void>;
  refetch: () => Promise<void>;
} {
  const [notes, setNotes] = useState<DirectedNoteRecord[]>([]);
  const inFlightRef = useRef(0);
  const tombstonesRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(async () => {
    inFlightRef.current += 1;
    try {
      const res = await directedNotesApi.inbox();
      const tombstoned = tombstonesRef.current;
      setNotes((prev) => {
        const fetched = res.notes.filter((n) => !tombstoned.has(n.id));
        const fetchedIds = new Set(fetched.map((n) => n.id));
        const wsOnly = prev.filter((n) => !fetchedIds.has(n.id) && !tombstoned.has(n.id));
        return [...wsOnly, ...fetched];
      });
    } catch {
      // best-effort — badge/inbox simply stay empty
    } finally {
      inFlightRef.current -= 1;
      // Window closes when no fetch is in flight — bounded set, and a
      // tombstoned id can't suppress unrelated future notes (cuids never recur,
      // but don't rely on that). Swap in a FRESH Set rather than mutating in
      // place: the setNotes updater above closes over `tombstoned` and may
      // run after this finally block (React defers the updater), so clearing
      // the same object in place would empty it out from under that closure.
      if (inFlightRef.current === 0) tombstonesRef.current = new Set();
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!wsClient) return;
    const offNew = wsClient.onPayload("directed_note", (payload) => {
      const n = payload as DirectedNoteRecord;
      if (!n?.id) return;
      setNotes((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
    });
    const offRetracted = wsClient.onPayload("directed_note_retracted", (payload) => {
      const id = (payload as { id?: string })?.id;
      if (!id) return;
      if (inFlightRef.current > 0) tombstonesRef.current.add(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    });
    const offAcked = wsClient.onPayload("directed_note_acked", (payload) => {
      const n = payload as DirectedNoteRecord;
      if (!n?.id) return;
      setNotes((prev) => prev.map((x) => (x.id === n.id ? n : x)));
    });
    return () => {
      offNew();
      offRetracted();
      offAcked();
    };
  }, [wsClient]);

  const acknowledge = useCallback(async (id: string, reply?: string) => {
    const { note } = await directedNotesApi.ack(id, reply);
    setNotes((prev) => prev.map((x) => (x.id === note.id ? note : x)));
  }, []);

  return {
    notes,
    activeCount: notes.filter(isDirectedNoteActive).length,
    acknowledge,
    refetch,
  };
}
